import { useId, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import type { ActualsResponse, KpiCatalogueEntry, OpenEvidence, EvidenceScopeType } from '../../types/strategicKpis';
import { formatKpiValue, noDataText, priorDirection, windowText } from '../../utils/strategicKpiFormat';
import { judgeGroup, lightOf, topGroup, scopeLabel, hasBabRows } from '../../utils/strategicKpiSelectors';
import { PriorCue, actualText, priorText, priorValueText, statusText, targetText } from './parts';

interface Props {
    data: ActualsResponse;
    context: KpiCatalogueEntry[];
    onOpenEvidence?: OpenEvidence;
}

const OPEN_KEY = 'strategic-kpis-context-open';

/**
 * The strip of the context KPIs (Success Rate, Avg Days — no target since definition version 4), above the table of
 * the steered KPIs (masterplan skpi-redesign E4, Q9). One card: collapsed it is one line — the name, the value and
 * the prior year of each KPI side by side; open it is a grid with the window as well. The items are in the DOM in
 * both states (the `data-skpi-*` contract of scripts/skpi-compare.mjs reads them whether the strip is open or not).
 * No status, no bar: a KPI without a target has no pace. A click on an item opens its evidence (E9) — the same
 * `onOpenEvidence` as a row of the table, so the page draws one panel for both. The open state is a per-viewer
 * convenience in `localStorage` (`strategic-kpis-context-open`, closed by default).
 */
export function ContextKpis({ data, context, onOpenEvidence }: Props) {
    const [open, setOpen] = useState<boolean>(() => { try { return window.localStorage.getItem(OPEN_KEY) === '1'; } catch { return false; } });
    const listId = useId();
    const top = topGroup(data);
    if (!top || context.length === 0) return null;
    const toggle = () => setOpen(o => { const n = !o; try { window.localStorage.setItem(OPEN_KEY, n ? '1' : '0'); } catch { /* ignore */ } return n; });
    const canOpen = onOpenEvidence && top.scopeType !== 'TOTAL';
    // S3-06: an empty year has no person — the person-level reason would be wrong at BU level.
    const emptyReason = top.personCount === 0 ? `Nobody is registered for ${data.year}` : null;
    const Chevron = open ? ChevronDown : ChevronRight;

    return (
        <section
            className="card"
            style={{ padding: open ? 'var(--p-space-12) var(--p-space-16) var(--p-space-16)' : 'var(--p-space-8) var(--p-space-16)' }}
            aria-label="Context KPIs"
            data-testid="context-kpis"
        >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
                <button type="button" onClick={toggle} className="btn quiet sm" aria-expanded={open} aria-controls={listId}>
                    <Chevron className="i sm" aria-hidden="true" />
                    Context KPIs <span className="muted">({context.length})</span>
                </button>
                {open && <span className="muted">Shown without a target — value and prior year only. These KPIs take no target (definition version 4).</span>}
                <ul
                    id={listId}
                    className={open
                        ? 'grid w-full grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3'
                        : 'flex min-w-0 flex-1 basis-0 flex-wrap items-center gap-x-6 gap-y-1'}
                >
                    {context.map(k => {
                        const j = judgeGroup(k, top.values[k.kpiKey]);
                        const dir = priorDirection(k, j.actual, j.prior);
                        const clickable = !!canOpen && k.groupAggregation !== 'derived' && (k.category !== 'bab' || hasBabRows(top));
                        const reason = j.actual === null ? (emptyReason ?? noDataText(j.noData)) : null;
                        const value: ReactNode = j.actual === null
                            ? <span className="muted inline-flex items-center gap-1" title={reason ?? undefined}>{actualText(k, j)} <Info className="i sm" aria-hidden="true" /></span>
                            : <span className="strong">{formatKpiValue(k.unit, j.actual)}</span>;
                        const prior = (
                            <span className="muted whitespace-nowrap" title={windowText(k, k.priorWindow)}>
                                PY {priorValueText(k, j)}
                                {dir && <PriorCue dir={dir} />}
                            </span>
                        );
                        // The contract strings (scripts/skpi-compare.mjs): the same helpers the cells use.
                        const skpiValue = actualText(k, j);
                        const skpiTarget = targetText(k, j);
                        const skpiStatus = statusText({ light: lightOf(j), achievement: j.achievement, hasTarget: j.hasTarget });
                        const skpiPrior = priorText(k, j);
                        const title = clickable ? `${k.definitionText} · opens the rows behind it` : k.definitionText;
                        const body = open ? (
                            <>
                                <span className="muted">{k.name}</span>
                                <span className="num" style={{ fontSize: 'var(--fs-h2)', lineHeight: 'var(--lh-h2)' }}>{value}</span>
                                {prior}
                                <span className="muted truncate" style={{ maxWidth: '100%' }} title={k.window.text}>
                                    {j.actual === null && reason ? reason : windowText(k, k.window)}
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="muted">{k.name}</span>
                                <span className="num">{value}</span>
                                {prior}
                            </>
                        );
                        const layout = open
                            ? 'flex min-w-0 flex-col items-start gap-1 text-left'
                            : 'flex min-w-0 items-baseline gap-2 whitespace-nowrap text-left';
                        const box = open ? { border: '1px solid var(--hair)', borderRadius: 'var(--card-radius)', padding: 'var(--p-space-12)', width: '100%' } : undefined;
                        return (
                            <li key={k.kpiKey} className="min-w-0">
                                {clickable ? (
                                    <button
                                        type="button"
                                        onClick={() => onOpenEvidence!(k.kpiKey, top.scopeType as EvidenceScopeType, top.scopeKey, scopeLabel(top), top.partial)}
                                        className={`${layout} cursor-pointer hover:underline`}
                                        style={box}
                                        title={title}
                                        data-skpi-kpi={k.kpiKey}
                                        data-skpi-context="1"
                                        data-skpi-value={skpiValue}
                                        data-skpi-target={skpiTarget}
                                        data-skpi-status={skpiStatus}
                                        data-skpi-prior={skpiPrior}
                                    >
                                        {body}
                                    </button>
                                ) : (
                                    <div
                                        className={layout}
                                        style={box}
                                        title={title}
                                        data-skpi-kpi={k.kpiKey}
                                        data-skpi-context="1"
                                        data-skpi-value={skpiValue}
                                        data-skpi-target={skpiTarget}
                                        data-skpi-status={skpiStatus}
                                        data-skpi-prior={skpiPrior}
                                    >
                                        {body}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </section>
    );
}
